(() => {
  'use strict';
  const storageKey = 'originaria:litq:language';
  const opaqueTags = new Set(['INPUT', 'SELECT', 'TEXTAREA']);
  const permittedAttributes = new Set(['aria-label', 'alt', 'title', 'placeholder']);
  const states = new Set();
  const originals = new WeakMap();
  let payload;
  let loading;
  let activeLanguage = 'en';

  const interpolate = (value, variables = {}) => value.replace(/\{([A-Za-z][A-Za-z0-9_]*)\}/g,
    (placeholder, key) => Object.hasOwn(variables, key) ? String(variables[key]) : placeholder);
  const translate = (key, fallback = key, variables = {}) => interpolate(
    payload?.catalogs?.[activeLanguage]?.[key] ?? payload?.catalogs?.en?.[key] ?? fallback, variables);
  window.LITQI18n = Object.freeze({
    t: translate,
    get language() { return activeLanguage; },
  });

  function collectSlots(element) {
    const slots = [];
    const visit = (parent, parentSlot = null) => {
      [...parent.children].forEach((child) => {
        const id = String(slots.length);
        slots.push({ id, node: child, tag: child.tagName.toLowerCase(), parent: parentSlot });
        if (!opaqueTags.has(child.tagName)) visit(child, id);
      });
    };
    visit(element);
    return slots;
  }

  // Translation HTML is parsed inertly, then checked against the finite source
  // schema. Only text nodes are new; all elements reuse their original nodes.
  function parsedMessage(value, schema) {
    const template = document.createElement('template');
    template.innerHTML = value;
    const expected = new Map((schema?.slots || []).map((slot) => [String(slot.id), slot]));
    const found = new Set();
    const inspect = (parent, parentSlot = null) => {
      [...parent.childNodes].forEach((node) => {
        if (node.nodeType === Node.TEXT_NODE) return;
        if (node.nodeType !== Node.ELEMENT_NODE) throw new Error('Unsupported translation node');
        const id = node.getAttribute('data-i18n-slot');
        const reference = expected.get(id);
        if (!reference || found.has(id) || node.attributes.length !== 1
          || node.attributes[0].name !== 'data-i18n-slot'
          || node.tagName.toLowerCase() !== reference.tag
          || (reference.parent ?? null) !== parentSlot) throw new Error('Translation markup differs from source');
        found.add(id);
        if (opaqueTags.has(node.tagName) && node.childNodes.length) throw new Error('Translated form content is forbidden');
        inspect(node, id);
      });
    };
    inspect(template.content);
    if (found.size !== expected.size) throw new Error('Missing translation slot');
    return template.content;
  }

  function applyMessage(element, key, value) {
    const schema = payload.metadata[key];
    if (!schema) return;
    let original = originals.get(element);
    if (!original) {
      original = { slots: collectSlots(element) };
      originals.set(element, original);
    }
    const sourceSlots = schema.slots || [];
    if (original.slots.length !== sourceSlots.length || !sourceSlots.every((slot, index) => {
      const actual = original.slots[index];
      return actual.id === String(slot.id) && actual.tag === slot.tag && actual.parent === (slot.parent ?? null);
    })) throw new Error('Translation hooks differ from source');
    const parsed = parsedMessage(value, schema);
    const nodes = new Map(original.slots.map((slot) => [slot.id, slot.node]));
    const render = (parent) => [...parent.childNodes].map((node) => {
      if (node.nodeType === Node.TEXT_NODE) return document.createTextNode(node.data);
      const originalNode = nodes.get(node.getAttribute('data-i18n-slot'));
      if (!opaqueTags.has(originalNode.tagName)) originalNode.replaceChildren(...render(node));
      return originalNode;
    });
    element.replaceChildren(...render(parsed));
  }

  function registryFor(root) {
    try {
      const element = root.querySelector('[data-litq-language-metadata]');
      return element ? JSON.parse(element.textContent) : null;
    } catch { return null; }
  }

  function languageFor(state, language) {
    return state.registry?.languages?.find((item) => item.id === language);
  }

  function updateRegistrationLinks(state, language) {
    state.root.querySelectorAll('[data-litq-registration-src], [data-litq-registration-fallback], .ac-litq__registration-fallback a').forEach((element) => {
      const attribute = element.matches('iframe') ? 'data-litq-registration-src' : 'href';
      const source = element.getAttribute(attribute);
      if (!source) return;
      try {
        const url = new URL(source, location.href);
        if (!url.pathname.endsWith('/register')) return;
        url.searchParams.set('lang', language);
        element.setAttribute(attribute, url.href);
        // An already-open registration form receives a language message without
        // navigating away from entered form data. The backend verifies origin.
        if (element.matches('iframe') && element.hasAttribute('src')) {
          element.contentWindow?.postMessage({ type: 'litq-language-change', language }, url.origin);
        }
      } catch { /* Keep the existing usable registration URL. */ }
    });
  }

  function apply(state, language) {
    const item = languageFor(state, language);
    if (!payload || !item || !payload.catalogs[language]) return;
    const root = state.root;
    const catalog = payload.catalogs[language];
    root.lang = item.locale || language;
    root.dir = item.direction || (language === 'ur' ? 'rtl' : 'ltr');
    root.dataset.litqLanguage = language;
    root.querySelectorAll('[data-i18n]').forEach((element) => {
      if (element.closest('[data-i18n-dynamic], [data-word], .ac-litq__preface-transcript, [data-litq-no-translate]')) return;
      const key = element.dataset.i18n;
      const value = catalog[key] ?? payload.catalogs.en[key];
      if (typeof value !== 'string') return;
      try { applyMessage(element, key, value); }
      catch (error) { console.warn('LITQ translation skipped:', key, error.message); }
    });
    root.querySelectorAll('[data-i18n-attrs]').forEach((element) => {
      if (element.closest('[data-word], .ac-litq__preface-transcript, [data-litq-no-translate]')) return;
      try {
        Object.entries(JSON.parse(element.dataset.i18nAttrs)).forEach(([attribute, key]) => {
          if (!permittedAttributes.has(attribute)) return;
          const value = catalog[key] ?? payload.catalogs.en[key];
          if (typeof value === 'string') element.setAttribute(attribute, value);
        });
      } catch { /* A malformed optional label must not interrupt the page. */ }
    });
    // Existing carousel geometry is LTR; cards retain the selected text direction.
    root.querySelectorAll('.ac-litq__carousel').forEach((track) => {
      track.dir = 'ltr';
      [...track.children].forEach((card) => { card.dir = root.dir; });
    });
    root.querySelectorAll('[data-preface-language]').forEach((article) => {
      const metadata = languageFor(state, article.dataset.prefaceLanguage);
      article.dir = metadata?.direction || 'ltr';
    });
    updateRegistrationLinks(state, language);
    root.querySelectorAll('[data-litq-image-toggle]').forEach((button) => {
      const paused = button.getAttribute('aria-pressed') === 'true';
      button.textContent = translate(paused ? 'ui.photos.resume' : 'ui.photos.pause', paused ? 'Resume image changes' : 'Pause image changes');
    });
    root.dispatchEvent(new CustomEvent('litq:translation-applied', { bubbles: true, detail: { language } }));
  }

  function fetchCatalog(state) {
    if (payload) return Promise.resolve(payload);
    if (!loading) {
      const url = state.root.dataset.litqTranslationsUrl;
      if (!url) return Promise.reject(new Error('Missing local translation asset URL'));
      const address = new URL(url, location.href);
      if (!['http:', 'https:'].includes(address.protocol)) return Promise.reject(new Error('Invalid translation URL'));
      loading = fetch(address.href, { credentials: 'omit' }).then((response) => {
        if (!response.ok) throw new Error('Translation asset unavailable');
        return response.json();
      }).then((data) => {
        if (data.version !== 1 || !data.catalogs?.en || !data.metadata) throw new Error('Invalid translation catalog');
        payload = data;
        return payload;
      }).catch((error) => { loading = null; throw error; });
    }
    return loading;
  }

  function emitRestore(language) {
    document.dispatchEvent(new CustomEvent('litq:language-change', { detail: { language, source: 'restore' } }));
  }

  function clearOpeningLanguage() {
    try {
      const url = new URL(location.href);
      if (!url.searchParams.has('lang')) return;
      url.searchParams.delete('lang');
      history.replaceState(history.state, '', url);
    } catch { /* English remains the opening language without history access. */ }
  }

  function initialise(scope) {
    const roots = [...scope.querySelectorAll('.ac-litq[data-litq-translations]')];
    if (scope.matches?.('.ac-litq[data-litq-translations]')) roots.unshift(scope);
    roots.forEach((root) => {
      if (root.dataset.translationsReady) return;
      const registry = registryFor(root);
      if (!registry?.languages?.length) return;
      root.dataset.translationsReady = 'true';
      const state = { root, registry };
      states.add(state);
      // Every new visit starts in English; language buttons still switch the page.
      activeLanguage = 'en';
      clearOpeningLanguage();
      fetchCatalog(state).then(() => {
        apply(state, activeLanguage);
        emitRestore(activeLanguage);
      }).catch(() => {
        activeLanguage = 'en';
        emitRestore('en');
      });
      // Image toggles update their English labels in their own script; translate
      // only this bounded dynamic control after that click has completed.
      root.addEventListener('click', (event) => {
        if (!event.target.closest('[data-litq-image-toggle]')) return;
        queueMicrotask(() => root.querySelectorAll('[data-litq-image-toggle]').forEach((button) => {
          const paused = button.getAttribute('aria-pressed') === 'true';
          button.textContent = translate(paused ? 'ui.photos.resume' : 'ui.photos.pause', paused ? 'Resume image changes' : 'Pause image changes');
        }));
      });
    });
  }

  document.addEventListener('litq:language-change', (event) => {
    const language = event.detail?.language;
    if (![...states].some((state) => languageFor(state, language))) return;
    activeLanguage = language;
    try { localStorage.setItem(storageKey, language); } catch { /* Keep working without persistence. */ }
    if (event.detail?.source !== 'restore') {
      try {
        const url = new URL(location.href);
        url.searchParams.set('lang', language);
        history.replaceState(history.state, '', url);
      } catch { /* Selection still works when history updates are unavailable. */ }
    }
    states.forEach((state) => {
      if (!state.root.isConnected) { states.delete(state); return; }
      if (payload) apply(state, language);
      else if (event.detail?.source !== 'restore') fetchCatalog(state).then(() => {
        apply(state, activeLanguage);
        emitRestore(activeLanguage);
      }).catch(() => { /* Preserve the readable English page and working audio. */ });
    });
  });
  initialise(document);
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    clearOpeningLanguage();
    emitRestore('en');
  });
  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
